import React, { useCallback, useEffect, useState } from 'react';
import { getPortions } from '../services/portionService';
import {
  createMenuProductPortion, updateMenuProductPortion, deleteMenuProductPortion,
} from '../services/menuProductPortionService';

const EMPTY_FORM = { portionId: '', quantity: '' };

export default function RecipeManager({ menuProductId, recipeItems, onChange }) {
  const [portions, setPortions] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingItem, setEditingItem] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadPortions = useCallback(() => {
    getPortions()
      .then(data => setPortions(Array.isArray(data) ? data.filter(p => p.state) : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadPortions();
  }, [loadPortions]);

  useEffect(() => {
    setForm(EMPTY_FORM);
    setEditingItem(null);
    setPendingDelete(null);
    setError('');
  }, [menuProductId]);

  function selectItem(item) {
    setError('');
    setPendingDelete(null);
    if (editingItem?.id === item.id) {
      setEditingItem(null);
      setForm(EMPTY_FORM);
    } else {
      setEditingItem(item);
      setForm({ portionId: String(item.portionId), quantity: String(item.quantity ?? '') });
    }
  }

  function cancelEdit() {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setPendingDelete(null);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingItem) {
        await updateMenuProductPortion(editingItem.id, { quantity: Number(form.quantity) });
      } else {
        await createMenuProductPortion({
          menuProductId,
          portionId: Number(form.portionId),
          quantity: Number(form.quantity),
        });
      }
      cancelEdit();
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo guardar el ítem de receta.');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setError('');
    setBusy(true);
    try {
      await deleteMenuProductPortion(pendingDelete.id);
      cancelEdit();
      await onChange();
    } catch (err) {
      setError(err.response?.data?.msg || 'No se pudo eliminar el ítem de receta.');
      setPendingDelete(null);
    } finally {
      setBusy(false);
    }
  }

  const usedPortionIds = new Set(recipeItems.map(r => r.portionId));
  const availablePortions = portions.filter(p => !usedPortionIds.has(p.id));

  return (
    <div>
      {error && (
        <div className="alert-orig alert-error" style={{ marginBottom: 14 }}>
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ marginBottom: 14 }}>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-6">
            <label className="form-label">Porción</label>
            {editingItem ? (
              <div style={{
                padding: '9px 12px', background: 'var(--orig-panel)',
                borderRadius: 'var(--orig-radius)', border: '1px solid var(--orig-border)',
                fontSize: 13.5, fontWeight: 500, color: 'var(--orig-text)',
              }}>
                {editingItem.portion?.name}
              </div>
            ) : (
              <select
                className="form-select"
                value={form.portionId}
                onChange={e => setForm(f => ({ ...f, portionId: e.target.value }))}
                required
              >
                <option value="">Seleccione una porción</option>
                {availablePortions.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.unitType ? ` — ${p.unitType}` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label">Cantidad</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              className="form-control"
              placeholder="0"
              value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))}
              required
            />
          </div>
          <div className="col-12" style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn-orig btn-orig-green" disabled={saving || busy}>
              <i className="bi bi-check-lg" />
              {editingItem ? 'Actualizar' : 'Agregar'}
            </button>
            {editingItem && (
              <button type="button" className="btn-orig btn-orig-outline" onClick={cancelEdit} disabled={saving || busy}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </form>

      {recipeItems.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--orig-text-muted)', fontSize: 13 }}>
          No hay porciones registradas en la receta
        </div>
      ) : (
        <div style={{ border: '1px solid var(--orig-border)', borderRadius: 'var(--orig-radius)', overflow: 'hidden' }}>
          {recipeItems.map((item, idx) => (
            <div key={item.id}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                  padding: '10px 14px',
                  borderBottom: idx < recipeItems.length - 1 || pendingDelete?.id === item.id ? '1px solid var(--orig-border)' : 'none',
                  background: editingItem?.id === item.id ? 'var(--orig-green-dim)' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--orig-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 120 }}>
                    {item.portion?.name}
                  </span>
                  <span className="badge-orig badge-green">
                    {item.quantity}{item.portion?.unitType ? ` ${item.portion.unitType}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <button
                    type="button"
                    className="btn-orig btn-orig-outline"
                    onClick={() => selectItem(item)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 11.5 }}
                    title="Editar cantidad"
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn-orig btn-orig-outline"
                    onClick={() => setPendingDelete(item)}
                    disabled={busy}
                    style={{ padding: '4px 10px', fontSize: 11.5, color: 'var(--orig-red-light)' }}
                    title="Eliminar de la receta"
                  >
                    <i className="bi bi-trash" />
                  </button>
                </div>
              </div>
              {pendingDelete?.id === item.id && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                  background: 'var(--orig-red-dim)',
                  borderBottom: idx < recipeItems.length - 1 ? '1px solid var(--orig-border)' : 'none',
                  padding: '10px 14px',
                }}>
                  <span style={{ fontSize: 13, color: 'var(--orig-text)' }}>
                    ¿Eliminar <strong>{pendingDelete.portion?.name}</strong> de la receta?
                  </span>
                  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                    <button type="button" className="btn-orig btn-orig-green" onClick={confirmDelete} disabled={busy}>
                      Sí, eliminar
                    </button>
                    <button type="button" className="btn-orig btn-orig-outline" onClick={() => setPendingDelete(null)} disabled={busy}>
                      No
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
